import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { IELDApp, IELDAsset } from '../../../../../../data/queries/eld-apps'
import type { TDispatcher } from '../../../../../../types/shipment'
import type { TActiveAssetType } from './eld-types'
import { useELDAppsData } from './use-eld-apps-data'
import { useELDAssetMutations } from './use-eld-asset-mutations'
import { useELDConfirmationState } from './use-eld-confirmation-state'

export type { TActiveAssetType } from './eld-types'

interface IUseELDAssetSelectionParams {
  currentTrailerNumber?: string | null
  currentTruckNumber?: string | null
  dispatchers?: TDispatcher[]
  isOpen: boolean
  onTrailerChange?: (trailerNumber: string | null) => void
  onTruckChange?: (truckNumber: string | null) => void
  shipmentID: string
  shipmentOwnerWorkspaceID: string
}

export const useELDAssetSelection = ({
  currentTrailerNumber,
  currentTruckNumber,
  dispatchers,
  isOpen,
  onTrailerChange,
  onTruckChange,
  shipmentID,
  shipmentOwnerWorkspaceID
}: IUseELDAssetSelectionParams) => {
  // Core state
  const [step, setStep] = useState<1 | 2>(1)
  const [activeAssetType, setActiveAssetType] = useState<TActiveAssetType>('trailer')
  const [selectedELDApp, setSelectedELDApp] = useState<IELDApp | null>(null)
  const [selectedItem, setSelectedItem] = useState<IELDAsset | null>(null)
  const [manualInputValue, setManualInputValue] = useState('')

  // Separate input values for step 1 (dual input mode)
  const [trailerInputValue, setTrailerInputValue] = useState('')
  const [truckInputValue, setTruckInputValue] = useState('')

  // Composed hooks
  const confirmationState = useELDConfirmationState()
  const mutations = useELDAssetMutations({
    currentTrailerNumber,
    currentTruckNumber,
    onTrailerChange,
    onTruckChange,
    shipmentID,
    shipmentOwnerWorkspaceID
  })
  const { assets, eldApps, loadingAssets, loadingELDApps, refetchELDApps } = useELDAppsData({
    activeAssetType,
    dispatchers,
    isOpen,
    selectedELDApp,
    shipmentID,
    shipmentOwnerWorkspaceID,
    step
  })

  const currentValue = activeAssetType === 'trailer' ? currentTrailerNumber : currentTruckNumber

  useEffect(() => {
    if (isOpen) {
      setManualInputValue(currentValue ?? '')
      setTrailerInputValue(currentTrailerNumber ?? '')
      setTruckInputValue(currentTruckNumber ?? '')
    }
  }, [isOpen, activeAssetType, currentValue, currentTrailerNumber, currentTruckNumber])

  // Step 2 handlers
  const handleELDAppSelect = (app: IELDApp) => {
    setSelectedELDApp(app)
    setSelectedItem(null)
    setStep(2)
  }

  const handleBack = () => {
    setStep(1)
    setSelectedELDApp(null)
    setSelectedItem(null)
    confirmationState.resetConfirmationState()
  }

  const handleActiveAssetTypeChange = (type: TActiveAssetType) => {
    setActiveAssetType(type)
    setSelectedItem(null)
    confirmationState.resetConfirmationState()
  }

  const handleAssetSelect = (asset: IELDAsset) => {
    setSelectedItem(asset)

    if (currentValue && currentValue !== asset.assetNumber) {
      confirmationState.showReplaceDialog(asset.assetNumber)
    }
  }

  const handleConfirmAsset = (onSuccess: () => void) => {
    if (!selectedItem) return

    if (currentValue && currentValue !== selectedItem.assetNumber && !confirmationState.showReplaceConfirmation) {
      confirmationState.showReplaceDialog(selectedItem.assetNumber)
      return
    }

    mutations.executeAssetUpdate(selectedItem.assetNumber, onSuccess, undefined, activeAssetType)
  }

  const handleRemoveClick = () => {
    confirmationState.showRemoveDialog()
  }

  const handleConfirmRemove = (onSuccess: () => void) => {
    mutations.executeAssetUpdate(null, onSuccess, undefined, activeAssetType)
  }

  const handleConfirmReplace = (onSuccess: () => void) => {
    if (confirmationState.pendingAction?.value) {
      mutations.executeAssetUpdate(confirmationState.pendingAction.value, onSuccess, undefined, activeAssetType)
    }
  }

  const handleCancelConfirmation = () => {
    confirmationState.cancelConfirmation()
  }

  const handleManualSubmit = (onSuccess: () => void) => {
    const trimmedValue = manualInputValue.trim()
    if (!trimmedValue) {
      toast.error(`Please enter a ${activeAssetType} number`)
      return
    }

    if (currentValue && currentValue !== trimmedValue) {
      confirmationState.showReplaceDialog(trimmedValue)
      return
    }

    mutations.executeAssetUpdate(trimmedValue, onSuccess, undefined, activeAssetType)
  }

  // Step 1 dual-input handlers
  const handleTrailerSubmit = (onSuccess?: () => void) => {
    const trimmedValue = trailerInputValue.trim()
    if (!trimmedValue) {
      toast.error('Please enter a trailer number')
      return
    }

    if (currentTrailerNumber && currentTrailerNumber !== trimmedValue) {
      confirmationState.showReplaceDialog(trimmedValue, 'trailer')
      return
    }

    mutations.executeAssetUpdate(trimmedValue, onSuccess, 'trailer', activeAssetType)
  }

  const handleTruckSubmit = (onSuccess?: () => void) => {
    const trimmedValue = truckInputValue.trim()
    if (!trimmedValue) {
      toast.error('Please enter a truck number')
      return
    }

    if (currentTruckNumber && currentTruckNumber !== trimmedValue) {
      confirmationState.showReplaceDialog(trimmedValue, 'truck')
      return
    }

    mutations.executeAssetUpdate(trimmedValue, onSuccess, 'truck', activeAssetType)
  }

  const handleTrailerRemoveClick = () => {
    confirmationState.showRemoveDialog('trailer')
  }

  const handleTruckRemoveClick = () => {
    confirmationState.showRemoveDialog('truck')
  }

  const handleDualConfirmRemove = (onSuccess?: () => void) => {
    if (confirmationState.confirmingAssetType) {
      mutations.executeAssetUpdate(null, onSuccess, confirmationState.confirmingAssetType, activeAssetType)
    }
  }

  const handleDualConfirmReplace = (onSuccess?: () => void) => {
    if (confirmationState.pendingAction?.value && confirmationState.confirmingAssetType) {
      mutations.executeAssetUpdate(
        confirmationState.pendingAction.value,
        onSuccess,
        confirmationState.confirmingAssetType,
        activeAssetType
      )
    }
  }

  const handleDualCancelConfirmation = () => {
    confirmationState.cancelConfirmation()
  }

  const resetState = () => {
    setStep(1)
    setActiveAssetType('trailer')
    setSelectedELDApp(null)
    setSelectedItem(null)
    setManualInputValue('')
    setTrailerInputValue('')
    setTruckInputValue('')
    mutations.setSelectingAsset(null)
    confirmationState.resetConfirmationState()
  }

  return {
    activeAssetType,
    assets,
    confirmingAssetType: confirmationState.confirmingAssetType,
    currentValue,
    eldApps,
    handleActiveAssetTypeChange,
    handleAssetSelect,
    handleBack,
    handleCancelConfirmation,
    handleConfirmAsset,
    handleConfirmRemove,
    handleConfirmReplace,
    handleDualCancelConfirmation,
    handleDualConfirmRemove,
    handleDualConfirmReplace,
    handleELDAppSelect,
    handleManualSubmit,
    handleRemoveClick,
    handleTrailerRemoveClick,
    handleTrailerSubmit,
    handleTruckRemoveClick,
    handleTruckSubmit,
    isLoadingUpdate: mutations.isLoadingUpdate,
    loadingAssets,
    loadingELDApps,
    manualInputValue,
    pendingAction: confirmationState.pendingAction,
    refetchELDApps,
    resetState,
    selectingAsset: mutations.selectingAsset,
    selectedELDApp,
    selectedItem,
    setManualInputValue,
    setSelectedItem,
    setTrailerInputValue,
    setTruckInputValue,
    showRemoveConfirmation: confirmationState.showRemoveConfirmation,
    showReplaceConfirmation: confirmationState.showReplaceConfirmation,
    step,
    trailerInputValue,
    truckInputValue
  }
}

export { clearELDAppsCache } from './eld-apps-cache'
